import { ElectiveSubjectsMapping } from '../../../types/electiveAssignmentType';
import { UpdateAssignmentRecord } from '../types/ElectiveAssignmentTypes';

const BASE_KEYS = new Set([
  'studentId',
  'studentName',
  'enrollmentNo',
  'rollNumber',
  'electives'
]);

const parseStudents = (response: any): ElectiveSubjectsMapping[] => {
  if (!response) return [];
  const responseBody = response?.data ?? response;
  if (Array.isArray(responseBody)) return responseBody;
  if (Array.isArray(responseBody?.data)) return responseBody.data;
  if (Array.isArray(responseBody?.payload)) return responseBody.payload;
  return [];
};

const isSubjectAssigned = (studentRecord: any, subjectKey: string): boolean => {
  const subjectCell = studentRecord?.[subjectKey];
  if (
    subjectCell &&
    typeof subjectCell === 'object' &&
    'isAssigned' in subjectCell
  ) {
    return Boolean(subjectCell.isAssigned);
  }
  return Array.isArray(studentRecord?.electives)
    ? studentRecord.electives.some(
        (elective: any) => elective?.subjectName === subjectKey
      )
    : false;
};

const getAllElectives = (students: ElectiveSubjectsMapping[]): string[] => {
  const electiveNames = new Set<string>();

  students.forEach((studentRecord) => {
    Object.entries(studentRecord).forEach(([fieldKey, fieldValue]) => {
      const isElectiveField =
        !BASE_KEYS.has(fieldKey) &&
        typeof fieldValue === 'object' &&
        fieldValue !== null &&
        ('isAssigned' in fieldValue || 'subjectId' in fieldValue);

      if (isElectiveField) {
        electiveNames.add(fieldKey);
      }
    });

    studentRecord.electives?.forEach((electiveEntry) => {
      if (electiveEntry?.subjectName) {
        electiveNames.add(electiveEntry.subjectName);
      }
    });
  });

  return Array.from(electiveNames);
};

const updateStudentRecord = (
  studentRecord: any,
  subjectKey: string,
  shouldAssign: boolean
) => {
  const nextStudent = { ...studentRecord };
  if (typeof nextStudent[subjectKey] === 'object') {
    nextStudent[subjectKey] = {
      ...nextStudent[subjectKey],
      isAssigned: shouldAssign
    };
  } else {
    const electivesList = [...(nextStudent.electives || [])];
    const electiveIndex = electivesList.findIndex(
      (elective: any) => elective.subjectName === subjectKey
    );
    if (shouldAssign && electiveIndex === -1) {
      electivesList.push({ subjectName: subjectKey, subjectId: subjectKey });
    }
    if (!shouldAssign && electiveIndex >= 0) {
      electivesList.splice(electiveIndex, 1);
    }
    nextStudent.electives = electivesList;
  }
  return nextStudent;
};

const buildStudentsMapping = (
  assignmentChanges: UpdateAssignmentRecord,
  currentStudents?: ElectiveSubjectsMapping[],
  _originalStudents?: ElectiveSubjectsMapping[]
) => {
  return Object.entries(assignmentChanges).map(([studentId, subjects]) => {
    const entry: any = { studentId };

    const changedSubjectIdsOrKeys = new Set<string>();
    Object.entries(subjects).forEach(([key, meta]) => {
      changedSubjectIdsOrKeys.add(key);
      if (meta.subjectId) changedSubjectIdsOrKeys.add(meta.subjectId);
    });

    const current = currentStudents?.find((student) => student.studentId === studentId);
    if (current) {
      Object.entries(current).forEach(([fieldKey, fieldValue]) => {
        if (
          !BASE_KEYS.has(fieldKey) &&
          typeof fieldValue === 'object' &&
          fieldValue !== null &&
          ('isAssigned' in fieldValue || 'subjectId' in fieldValue)
        ) {
          const meta: any = fieldValue;
          const subjectId = meta.subjectId || fieldKey;
          if (
            subjectId &&
            !changedSubjectIdsOrKeys.has(subjectId) &&
            !changedSubjectIdsOrKeys.has(fieldKey)
          ) {
            entry[subjectId] = {
              isAssigned: Boolean(meta.isAssigned),
              id: meta.id
            };
          }
        }
      });

      (current.electives || []).forEach((elective: any) => {
        const subjectId = elective?.subjectId || elective?.subjectName;
        if (
          subjectId &&
          !entry[subjectId] &&
          !changedSubjectIdsOrKeys.has(subjectId) &&
          !changedSubjectIdsOrKeys.has(elective?.subjectName)
        ) {
          entry[subjectId] = { isAssigned: true, id: elective?.id };
        }
      });
    }

    Object.entries(subjects).forEach(([subjectKey, meta]) => {
      const subjectId = meta.subjectId || subjectKey;
      entry[subjectId] = { isAssigned: meta.current, id: meta.id };
    });

    return entry;
  });
};

export {
  BASE_KEYS,
  isSubjectAssigned,
  getAllElectives,
  parseStudents,
  buildStudentsMapping,
  updateStudentRecord
};
